import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import EventFormDialog from '../components/EventFormDialog';
import ManualBookingDialog from '../components/ManualBookingDialog';
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  LinearProgress,
  Avatar,
  Tooltip,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Skeleton,
} from '@mui/material';
import {
  Search,
  Edit,
  Delete,
  Event,
  Add,
  OpenInNew,
  CalendarToday,
  AccessTime,
  CurrencyRupee,
  Paid,
  AccountBalanceWallet,
  Groups,
  LocationOn,
  Refresh,
  Close,
  FileDownload,
  ContentCopy,
  PersonAdd,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { bookingStudioOf } from '../utils/studioOf';
import { colors } from '../theme/theme';
import { isOwnerRole } from '../utils/roles';
import { useMyLocations } from '../hooks/useMyLocations';
import BranchChip from '../components/BranchChip';

interface EventItem {
  _id: string;
  eventName: string;
  date: string;
  price: number;
  slots: number;
  images: string[];
  description: string;
  skillLevel: string;
  location?: string;
  interestedUsers?: any[];
  bookingCount?: number;
  razorpayRevenue?: number;
  studio?: {
    _id: string;
    studioName: string;
    whitelabelConfig?: { appSource?: string; appName?: string };
  };
  instructor?: { _id: string; name: string };
  classType?: { title: string };
  isPublished?: boolean;
  isDraft?: boolean;
}

interface AttendeeBooking {
  _id: string;
  bookingId?: string;
  bookingStatus: string;
  user: {
    _id: string;
    name: string;
    email: string;
    phoneNumber?: string;
    image?: string;
  };
  quantity?: number;
  transaction?: {
    _id: string;
    basePrice?: number;
    modeOfPayment?: string;
  };
  createdAt?: string;
}

const bkStatusChip = (status: string): { label: string; color: string; bg: string } => {
  const map: Record<string, { label: string; color: string; bg: string }> = {
    BOOKED:     { label: 'Booked',     color: '#1565C0', bg: '#E3F2FD' },
    CHECKED_IN: { label: 'Checked In', color: '#2E7D32', bg: '#E8F5E9' },
    CANCELLED:  { label: 'Cancelled',  color: '#C62828', bg: '#FFEBEE' },
    NO_SHOW:    { label: 'No Show',    color: '#5D4037', bg: '#EFEBE9' },
    COMPLETED:  { label: 'Completed',  color: '#7B1FA2', bg: '#F3E5F5' },
  };
  return map[status] ?? { label: status, color: '#616161', bg: '#F5F5F5' };
};

// Events store date as naive IST (IST hours stored as-if UTC) — display raw UTC
const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
const fmtTime = (d: string) =>
  new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'UTC' });

const getStatus = (event: EventItem): { label: string; color: string; bg: string } => {
  const isPast = new Date(event.date) < new Date(Date.now() - 5.5 * 3600000); // naive IST < now IST
  if (isPast) return { label: 'Past', color: '#78909C', bg: '#ECEFF1' };
  if (event.isDraft) return { label: 'Draft', color: '#9E9E9E', bg: '#F5F5F5' };
  const booked = event.bookingCount ?? event.interestedUsers?.length ?? 0;
  const total = event.slots ?? 0;
  const pct = total > 0 ? booked / total : 0;
  if (pct >= 1) return { label: 'Sold Out', color: '#E53935', bg: '#FFEBEE' };
  if (pct >= 0.75) return { label: 'Selling fast', color: '#F57C00', bg: '#FFF3E0' };
  return { label: 'On sale', color: '#2E7D32', bg: '#E8F5E9' };
};

const studioColor = (name: string) => {
  const palette = ['#6C63FF', '#E91E63', '#00BCD4', '#FF9800', '#4CAF50', '#9C27B0'];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffffffff;
  return palette[Math.abs(h) % palette.length];
};

// Two-tone neutral palette for user avatars — less visual noise than per-user hue
const userAvatarColor = (name: string) =>
  (name.charCodeAt(0) + (name.charCodeAt(1) || 0)) % 2 === 0 ? '#5C6BC0' : '#546E7A';

const PAGE_SIZE = 20;
const RAZORPAY_FEE_RATE = 0.025;

const roundCurrency = (value: number) => Math.round(value * 100) / 100;

const netAfterRazorpayFee = (collected: number) =>
  roundCurrency(collected * (1 - RAZORPAY_FEE_RATE));

interface RevenueStatBoxProps {
  label: string;
  amount: number;
  tooltip: string;
  sublabel?: string;
  icon?: React.ReactNode;
}

const RevenueStatBox: React.FC<RevenueStatBoxProps> = ({
  label,
  amount,
  tooltip,
  sublabel,
  icon,
}) => {
  const hasAmount = amount > 0;

  return (
    <Tooltip title={tooltip}>
      <Box
        sx={{
          flex: 1,
          minWidth: { xs: '100%', sm: 160 },
          px: 2,
          py: 1.75,
          borderRadius: 2,
          bgcolor: hasAmount ? 'rgba(46, 125, 50, 0.06)' : '#F7F7F7',
          border: '1px solid',
          borderColor: hasAmount ? 'rgba(46, 125, 50, 0.22)' : '#E8E8E8',
          cursor: 'default',
        }}
      >
        <Box display="flex" alignItems="center" gap={0.75} mb={0.75}>
          {icon}
          <Typography
            variant="caption"
            sx={{
              color: hasAmount ? '#558B2F' : '#9E9E9E',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.6,
              fontSize: '0.68rem',
            }}
          >
            {label}
          </Typography>
        </Box>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '1.25rem',
            color: hasAmount ? '#2E7D32' : '#757575',
            lineHeight: 1.15,
            letterSpacing: '-0.01em',
          }}
        >
          ₹{amount.toLocaleString('en-IN')}
        </Typography>
        {sublabel && (
          <Typography
            variant="caption"
            sx={{ color: '#9E9E9E', fontSize: '0.72rem', mt: 0.5, display: 'block' }}
          >
            {sublabel}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

const EventsPage: React.FC = () => {
  // Branch identity per row while "All locations" aggregates them.
  const { byId: locationById, isAllLocations } = useMyLocations();
  const eventStudioId = (ev: any): string | undefined => {
    const v = ev?.studio;
    return v ? String(v?._id ?? v) : undefined;
  };

  const navigate = useNavigate();
  const location = useLocation();
  const { admin } = useAuth();
  const isStudio = isOwnerRole(admin?.role) || admin?.role === 'manager' || admin?.role === 'instructor';
  const isSuperAdmin = admin?.role === 'super_admin' || admin?.role === 'admin';

  const [events, setEvents] = useState<EventItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'upcoming' | 'past' | 'selling_fast'>('all');
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  // Source event for "Duplicate" — opens the form pre-filled as a new (not-live) event.
  const [duplicateEvent, setDuplicateEvent] = useState<any>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<EventItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Attendees dialog
  const [attendeesDialogOpen, setAttendeesDialogOpen] = useState(false);
  const [attendeesEvent, setAttendeesEvent] = useState<EventItem | null>(null);
  const [attendees, setAttendees] = useState<AttendeeBooking[]>([]);
  const [attendeesLoading, setAttendeesLoading] = useState(false);
  const [attendeesSearchTerm, setAttendeesSearchTerm] = useState('');

  // Status update sub-dialog (inside attendees dialog)
  const [attStatusDialogOpen, setAttStatusDialogOpen] = useState(false);
  const [attBookingToUpdate, setAttBookingToUpdate] = useState<AttendeeBooking | null>(null);
  const [attNewStatus, setAttNewStatus] = useState('');
  const [attStatusReason, setAttStatusReason] = useState('');
  const [attCreditClassBack, setAttCreditClassBack] = useState(true);
  const [isUpdatingAttStatus, setIsUpdatingAttStatus] = useState(false);

  const [manualBookingOpen, setManualBookingOpen] = useState(false);
  const [manualBookingEvent, setManualBookingEvent] = useState<{
    _id: string;
    eventName: string;
    date?: string;
    price?: number;
    slots?: number;
    availableSlots?: number;
  } | null>(null);
  const [manualBookingStudio, setManualBookingStudio] = useState<{
    _id: string;
    studioName: string;
    whitelabelConfig?: { appSource?: string; appName?: string };
  } | null>(null);

  const studioId = (admin as any)?.studio?._id || '';

  const fetchIdRef = useRef(0);
  const pageRef = useRef(1);
  const hasMoreRef = useRef(true);
  const loadingMoreRef = useRef(false);
  const isInitialLoadingRef = useRef(true);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const fetchEventsRef = useRef<((pageNum?: number, reset?: boolean) => Promise<void>) | undefined>(undefined);

  useEffect(() => {
    if ((location.state as any)?.openCreate) {
      setCreateDialogOpen(true);
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  const openManualBooking = (ev: EventItem) => {
    const booked = ev.bookingCount ?? ev.interestedUsers?.length ?? 0;
    setManualBookingEvent({
      _id: ev._id,
      eventName: ev.eventName,
      date: ev.date,
      price: ev.price,
      slots: ev.slots,
      availableSlots: Math.max(0, (ev.slots ?? 0) - booked),
    });
    if (ev.studio?._id) {
      setManualBookingStudio({
        _id: ev.studio._id,
        studioName: ev.studio.studioName,
        whitelabelConfig: ev.studio.whitelabelConfig,
      });
    } else {
      setManualBookingStudio(null);
    }
    setManualBookingOpen(true);
  };

  const fetchEvents = async (pageNum: number = 1, reset: boolean = true) => {
    if (reset) {
      isInitialLoadingRef.current = true;
      loadingMoreRef.current = false;
      hasMoreRef.current = true;
      pageRef.current = 1;
    } else if (isInitialLoadingRef.current || loadingMoreRef.current || !hasMoreRef.current) {
      return;
    } else {
      loadingMoreRef.current = true;
    }
    const fetchId = ++fetchIdRef.current;
    try {
      if (reset) {
        setIsLoading(true);
        setIsLoadingMore(false);
      } else {
        setIsLoadingMore(true);
      }
      setError(null);

      const params: Record<string, string | number> = { page: pageNum, pageSize: PAGE_SIZE };
      const q = searchTerm.trim();
      if (q) params.search = q;
      if (statusFilter === 'upcoming' || statusFilter === 'selling_fast') params.status = 'upcoming';
      if (statusFilter === 'past') params.status = 'past';

      const response = await apiService.getEvents(params);
      if (fetchId !== fetchIdRef.current) return;

      const newEvents: EventItem[] = response.events || [];
      const count = response.count ?? newEvents.length;
      const pages = response.pages ?? Math.max(1, Math.ceil(count / PAGE_SIZE) || 1);
      const more = pageNum < pages;

      setEvents(prev => (reset ? newEvents : [...prev, ...newEvents]));
      setTotalCount(count);
      pageRef.current = pageNum;
      hasMoreRef.current = more;
      setHasMore(more);
    } catch (err: any) {
      if (fetchId !== fetchIdRef.current) return;
      setError(err.message || 'Failed to fetch events');
      if (reset) setEvents([]);
    } finally {
      if (fetchId === fetchIdRef.current) {
        isInitialLoadingRef.current = false;
        loadingMoreRef.current = false;
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    }
  };
  fetchEventsRef.current = fetchEvents;

  useEffect(() => {
    fetchEvents(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, statusFilter]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || isLoading || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        if (isInitialLoadingRef.current || loadingMoreRef.current || !hasMoreRef.current) return;
        fetchEventsRef.current?.(pageRef.current + 1, false);
      },
      { rootMargin: '240px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [isLoading, hasMore, events.length]);

  const confirmDelete = async () => {
    if (!eventToDelete) return;
    try {
      setIsDeleting(true);
      await apiService.deleteEvent(eventToDelete._id, eventStudioId(eventToDelete));
      setEvents(prev => prev.filter(e => e._id !== eventToDelete._id));
      setTotalCount(prev => Math.max(0, prev - 1));
      setDeleteDialogOpen(false);
      setEventToDelete(null);
    } catch (err: any) {
      console.error('Delete event error:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const openAttendeesDialog = async (ev: EventItem) => {
    setAttendeesEvent(ev);
    setAttendeesDialogOpen(true);
    setAttendeesLoading(true);
    setAttendeesSearchTerm('');
    setAttendees([]);
    try {
      const res = await apiService.getEventBookings(ev._id);
      setAttendees(res.bookings || []);
    } catch (err: any) {
      console.error('Failed to fetch attendees:', err);
    } finally {
      setAttendeesLoading(false);
    }
  };

  // Export all bookings for the currently-open event to CSV, including the
  // quantity (tickets) each user purchased. Built from the already-loaded
  // attendee data — no extra request.
  const exportAttendeesCSV = () => {
    if (!attendeesEvent || attendees.length === 0) return;

    const headers = [
      'Booking ID',
      'Name',
      'Email',
      'Phone',
      'Quantity',
      'Status',
      'Payment Mode',
      'Amount',
      'Booked At',
    ];
    const escape = (v: any) => {
      const s = v === undefined || v === null ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = attendees.map((b) => [
      b.bookingId || b._id,
      b.user?.name || '',
      b.user?.email || '',
      b.user?.phoneNumber || '',
      b.quantity ?? 1,
      b.bookingStatus,
      b.transaction?.modeOfPayment || '',
      b.transaction?.basePrice ?? '',
      b.createdAt ? new Date(b.createdAt).toLocaleString('en-IN') : '',
    ]);

    const totalQty = attendees.reduce((sum, b) => sum + (b.quantity ?? 1), 0);
    const csv = [
      headers.map(escape).join(','),
      ...rows.map((r) => r.map(escape).join(',')),
      '',
      `Total bookings,${attendees.length}`,
      `Total tickets,${totalQty}`,
    ].join('\n');

    const safeName = (attendeesEvent.eventName || 'event').replace(/[^a-z0-9]+/gi, '_');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bookings_${safeName}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const openAttStatusDialog = (booking: AttendeeBooking) => {
    setAttBookingToUpdate(booking);
    setAttNewStatus(booking.bookingStatus);
    setAttStatusReason('');
    setAttCreditClassBack(booking.bookingStatus !== 'CANCELLED');
    setAttStatusDialogOpen(true);
  };

  const handleAttStatusConfirm = async () => {
    if (!attBookingToUpdate || !attNewStatus) return;
    setIsUpdatingAttStatus(true);
    try {
      const goingToCancel = attNewStatus === 'CANCELLED' && attBookingToUpdate.bookingStatus !== 'CANCELLED';
      const goingFromCancel =
        attBookingToUpdate.bookingStatus === 'CANCELLED' &&
        (attNewStatus === 'BOOKED' || attNewStatus === 'CHECKED_IN');
      const sendCreditClassBack = (goingToCancel || goingFromCancel) ? attCreditClassBack : undefined;
      await apiService.updateBookingStatus(
        attBookingToUpdate._id,
        attNewStatus,
        attStatusReason || undefined,
        sendCreditClassBack,
        // Pinned — under "All locations" the switcher sends __all__, which
        // the server refuses for writes.
        bookingStudioOf(attBookingToUpdate) || (attendeesEvent ? eventStudioId(attendeesEvent) : null),
      );
      setAttendees(prev =>
        prev.map(b => b._id === attBookingToUpdate._id ? { ...b, bookingStatus: attNewStatus } : b),
      );
      setAttStatusDialogOpen(false);
      setAttBookingToUpdate(null);
    } catch (err: any) {
      console.error('Failed to update status:', err);
    } finally {
      setIsUpdatingAttStatus(false);
    }
  };

  const filteredAttendees = useMemo(() => {
    if (!attendeesSearchTerm.trim()) return attendees;
    const q = attendeesSearchTerm.toLowerCase();
    return attendees.filter(b =>
      (b.user?.name || '').toLowerCase().includes(q) ||
      (b.user?.email || '').toLowerCase().includes(q) ||
      (b.user?.phoneNumber || '').toLowerCase().includes(q),
    );
  }, [attendees, attendeesSearchTerm]);

  const displayedEvents = useMemo(() => {
    if (statusFilter !== 'selling_fast') return events;
    return events.filter(ev => {
      const booked = ev.bookingCount ?? ev.interestedUsers?.length ?? 0;
      const pct = ev.slots > 0 ? booked / ev.slots : 0;
      return pct >= 0.75;
    });
  }, [events, statusFilter]);

  return (
    <Box sx={{ bgcolor: '#FAFAFA', minHeight: '100vh', p: 3 }}>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="text.primary">
            Events
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            {isLoading
              ? 'Loading…'
              : statusFilter === 'selling_fast'
              // No server-side count for this filter (it's a client-side ≥75%-booked
              // check on top of the "upcoming" page) — totalCount here is the upcoming
              // total, not the selling-fast total, so show what's actually been found.
              ? `${displayedEvents.length.toLocaleString('en-IN')}${hasMore ? '+' : ''} selling fast`
              : `${totalCount.toLocaleString('en-IN')} ${statusFilter === 'all' ? 'total' : statusFilter.replace('_', ' ')}${
                  events.length < totalCount ? ` · ${events.length} loaded` : ''
                }`}
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={() => fetchEvents(1, true)} color="primary">
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<Add />} onClick={() => setCreateDialogOpen(true)} sx={{ fontWeight: 600 }}>
            Create Event
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Search + Filter bar */}
      <Box display="flex" gap={2} alignItems="center" mb={3} flexWrap="wrap">
        <TextField
          placeholder="Search events by name or studio…"
          value={searchInput}
          onChange={(e) => {
            const value = e.target.value;
            setSearchInput(value);
            if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
            searchDebounceRef.current = setTimeout(() => setSearchTerm(value), 400);
          }}
          size="small"
          sx={{ flex: '1 1 280px', bgcolor: '#fff', borderRadius: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Box display="flex" gap={1} flexWrap="wrap">
          {(['all', 'upcoming', 'past', 'selling_fast'] as const).map((f) => (
            <Chip
              key={f}
              label={f === 'all' ? 'All' : f === 'upcoming' ? 'Upcoming' : f === 'past' ? 'Past' : 'Selling Fast'}
              onClick={() => setStatusFilter(f)}
              variant={statusFilter === f ? 'filled' : 'outlined'}
              sx={{
                fontWeight: 600,
                cursor: 'pointer',
                bgcolor: statusFilter === f ? colors.primary : '#fff',
                color: statusFilter === f ? '#fff' : 'text.secondary',
                borderColor: statusFilter === f ? colors.primary : '#e0e0e0',
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Event List */}
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
          <CircularProgress />
        </Box>
      ) : displayedEvents.length === 0 && !hasMore && !isLoadingMore ? (
        <Box
          sx={{
            border: '1px dashed #D0D0D0',
            borderRadius: 3,
            p: 6,
            textAlign: 'center',
            bgcolor: '#fff',
          }}
        >
          <Event sx={{ fontSize: 52, color: '#D0D0D0', mb: 1.5 }} />
          <Typography variant="h6" color="text.secondary" fontWeight={500}>
            {searchInput ? 'No events match your search' : 'No events yet'}
          </Typography>
          {!searchInput && statusFilter === 'all' && (
            <Button variant="contained" startIcon={<Add />} sx={{ mt: 2 }} onClick={() => setCreateDialogOpen(true)}>
              Create your first event
            </Button>
          )}
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" gap={2}>
          {displayedEvents.map((ev) => {
            const booked = ev.bookingCount ?? ev.interestedUsers?.length ?? 0;
            const total = ev.slots ?? 0;
            const soldPct = total > 0 ? Math.round((booked / total) * 100) : 0;
            const status = getStatus(ev);
            const isPast = status.label === 'Past';
            const studioName = ev.studio?.studioName || '';
            const avatarColor = studioColor(studioName);
            const collectedRevenue = ev.razorpayRevenue ?? 0;
            const netRevenue = netAfterRazorpayFee(collectedRevenue);
            const collectedIconSx = {
              fontSize: 18,
              color: collectedRevenue > 0 ? '#388E3C' : '#9E9E9E',
            };
            const netIconSx = {
              fontSize: 18,
              color: netRevenue > 0 ? '#388E3C' : '#9E9E9E',
            };

            return (
              <Box
                key={ev._id}
                sx={{
                  display: 'flex',
                  bgcolor: '#fff',
                  borderRadius: 3,
                  border: '1px solid #EBEBEB',
                  overflow: 'hidden',
                  opacity: isPast ? 0.75 : 1,
                  transition: 'box-shadow 0.18s, border-color 0.18s',
                  '&:hover': {
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    borderColor: '#D0D0D0',
                  },
                }}
              >
                {/* Thumbnail */}
                <Box
                  sx={{
                    width: 180,
                    minWidth: 180,
                    position: 'relative',
                    flexShrink: 0,
                    bgcolor: '#F3F0F5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                  }}
                >
                  {ev.images?.[0] ? (
                    <Box
                      component="img"
                      src={ev.images[0]}
                      alt={ev.eventName}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0 }}
                    />
                  ) : (
                    <Event sx={{ fontSize: 40, color: '#C5B8CC' }} />
                  )}
                  {!ev.images?.[0] && (
                    <Typography
                      variant="caption"
                      sx={{
                        position: 'absolute',
                        bottom: 8,
                        color: '#B0A0BB',
                        letterSpacing: 1,
                        fontSize: '0.6rem',
                        textTransform: 'uppercase',
                        fontWeight: 600,
                      }}
                    >
                      Event Poster
                    </Typography>
                  )}
                </Box>

                {/* Content */}
                <Box flex={1} px={3} py={2.5} minWidth={0}>

                  {/* Name + chips */}
                  <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap" mb={0.75}>
                    <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ lineHeight: 1.3 }}>
                      {ev.eventName}
                    </Typography>
                    <Chip
                      label={status.label}
                      size="small"
                      sx={{
                        bgcolor: status.bg,
                        color: status.color,
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        height: 22,
                        borderRadius: 1,
                      }}
                    />
                    {/* Under "All locations" the branch name is what tells
                        two branches' events apart — `studioName` can be the
                        same string across siblings of one brand. */}
                    {isAllLocations && locationById(eventStudioId(ev)) ? (
                      <BranchChip location={locationById(eventStudioId(ev))} />
                    ) : studioName && (
                      <Chip
                        avatar={
                          <Avatar sx={{ bgcolor: avatarColor, width: 18, height: 18, fontSize: '0.6rem', fontWeight: 700 }}>
                            {studioName.charAt(0).toUpperCase()}
                          </Avatar>
                        }
                        label={studioName}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.72rem', height: 22, borderRadius: 1, borderColor: '#E0E0E0', color: 'text.secondary' }}
                      />
                    )}
                    {ev.classType && (
                      <Chip
                        label={ev.classType.title}
                        size="small"
                        sx={{ bgcolor: `${colors.primary}12`, color: colors.primary, fontSize: '0.7rem', height: 22, borderRadius: 1, fontWeight: 600 }}
                      />
                    )}
                  </Box>

                  {/* Description */}
                  {ev.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      mb={1.5}
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        fontSize: '0.82rem',
                      }}
                    >
                      {ev.description}
                    </Typography>
                  )}

                  {/* Metadata row */}
                  <Box display="flex" gap={2.5} alignItems="center" flexWrap="wrap" mb={1.5}>
                    {studioName && (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <LocationOn sx={{ fontSize: 14, color: '#9E9E9E' }} />
                        <Typography variant="caption" color="text.secondary" fontWeight={500}>{studioName}</Typography>
                      </Box>
                    )}
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <CalendarToday sx={{ fontSize: 14, color: '#9E9E9E' }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>{fmtDate(ev.date)}</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <AccessTime sx={{ fontSize: 14, color: '#9E9E9E' }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>{fmtTime(ev.date)}</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <CurrencyRupee sx={{ fontSize: 14, color: '#9E9E9E' }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        {ev.price.toLocaleString('en-IN')}
                      </Typography>
                    </Box>
                    {ev.instructor && (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Groups sx={{ fontSize: 14, color: '#9E9E9E' }} />
                        <Typography variant="caption" color="text.secondary" fontWeight={500}>{ev.instructor.name}</Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Ticket progress */}
                  {total > 0 && (
                    <Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(soldPct, 100)}
                        sx={{
                          height: 5,
                          borderRadius: 3,
                          mb: 0.5,
                          bgcolor: '#F0EBF4',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 3,
                            bgcolor:
                              soldPct >= 100 ? '#E53935'
                              : soldPct >= 75 ? '#F57C00'
                              : colors.primary,
                          },
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        <strong style={{ color: 'text.primary' }}>{booked}</strong> / {total} tickets · {soldPct}% sold
                      </Typography>
                    </Box>
                  )}

                  {/* Revenue stats */}
                  <Box
                    display="flex"
                    gap={1.5}
                    mt={1.75}
                    flexWrap="wrap"
                    sx={{ maxWidth: isSuperAdmin ? 420 : 220 }}
                  >
                    <RevenueStatBox
                      label="Collected"
                      amount={collectedRevenue}
                      tooltip="Real payments only — excludes free bookings, cash, subscriptions, and manually recorded bookings"
                      sublabel="Confirmed online payments"
                      icon={<Paid sx={collectedIconSx} />}
                    />
                    {isSuperAdmin && (
                      <RevenueStatBox
                        label="Net payout"
                        amount={netRevenue}
                        tooltip="Collected amount minus 2.5% Razorpay payment gateway fee"
                        sublabel="After 2.5% gateway fee"
                        icon={<AccountBalanceWallet sx={netIconSx} />}
                      />
                    )}
                  </Box>
                </Box>

                {/* Actions */}
                <Divider orientation="vertical" flexItem sx={{ borderColor: '#F0F0F0' }} />
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  gap={1}
                  px={2}
                  py={2}
                  sx={{ minWidth: 142 }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Edit sx={{ fontSize: 15 }} />}
                    onClick={() => navigate(`/events/${ev._id}`)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.78rem',
                      borderColor: '#E0E0E0',
                      color: 'text.primary',
                      justifyContent: 'flex-start',
                      '&:hover': { borderColor: colors.primary, color: colors.primary },
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ContentCopy sx={{ fontSize: 15 }} />}
                    onClick={() => setDuplicateEvent(ev)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.78rem',
                      borderColor: '#E0E0E0',
                      color: 'text.primary',
                      justifyContent: 'flex-start',
                      '&:hover': { borderColor: colors.primary, color: colors.primary },
                    }}
                  >
                    Duplicate
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Groups sx={{ fontSize: 15 }} />}
                    onClick={() => openAttendeesDialog(ev)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.78rem',
                      borderColor: '#E0E0E0',
                      color: 'text.primary',
                      justifyContent: 'flex-start',
                      '&:hover': { borderColor: colors.primary, color: colors.primary },
                    }}
                  >
                    Attendees {booked > 0 && `(${booked})`}
                  </Button>
                  {!isPast && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<PersonAdd sx={{ fontSize: 15 }} />}
                      onClick={() => openManualBooking(ev)}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.78rem',
                        borderColor: '#E0E0E0',
                        color: 'text.primary',
                        justifyContent: 'flex-start',
                        '&:hover': { borderColor: colors.primary, color: colors.primary },
                      }}
                    >
                      Manual Booking
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<OpenInNew sx={{ fontSize: 15 }} />}
                    onClick={() => navigate(`/events/${ev._id}`)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.78rem',
                      borderColor: '#E0E0E0',
                      color: 'text.primary',
                      justifyContent: 'flex-start',
                      '&:hover': { borderColor: colors.primary, color: colors.primary },
                    }}
                  >
                    Open
                  </Button>
                  {!isStudio && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Delete sx={{ fontSize: 15 }} />}
                      onClick={() => { setEventToDelete(ev); setDeleteDialogOpen(true); }}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.78rem',
                        borderColor: '#FFCDD2',
                        color: '#E53935',
                        justifyContent: 'flex-start',
                        '&:hover': { borderColor: '#E53935', bgcolor: '#FFEBEE' },
                      }}
                    >
                      Delete
                    </Button>
                  )}
                </Box>
              </Box>
            );
          })}
          {displayedEvents.length === 0 && (hasMore || isLoadingMore) && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          )}
          <Box ref={sentinelRef} sx={{ height: 1 }} />
          {isLoadingMore && displayedEvents.length > 0 && (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={28} />
            </Box>
          )}
        </Box>
      )}

      {/* Create Event Dialog */}
      {createDialogOpen && (
        <EventFormDialog
          open={createDialogOpen}
          onClose={() => setCreateDialogOpen(false)}
          onSuccess={fetchEvents}
          studioId={studioId}
        />
      )}

      {/* Duplicate Event Dialog — create-mode, pre-filled from the source event (not live) */}
      {duplicateEvent && (
        <EventFormDialog
          open={Boolean(duplicateEvent)}
          onClose={() => setDuplicateEvent(null)}
          onSuccess={() => { setDuplicateEvent(null); fetchEvents(); }}
          duplicateFrom={duplicateEvent}
          studioId={studioId}
        />
      )}

      {/* Attendees Dialog */}
      <Dialog
        open={attendeesDialogOpen}
        onClose={() => setAttendeesDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, maxHeight: '90vh' } }}
      >
        <DialogTitle sx={{ pb: 1.5, pr: 2 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h6" fontWeight={700}>{attendeesEvent?.eventName}</Typography>
              <Typography variant="caption" color="text.secondary">
                {attendeesEvent ? `${fmtDate(attendeesEvent.date)} · ${fmtTime(attendeesEvent.date)}` : ''}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Button
                onClick={exportAttendeesCSV}
                disabled={attendeesLoading || attendees.length === 0}
                variant="outlined"
                size="small"
                startIcon={<FileDownload fontSize="small" />}
              >
                Export CSV
              </Button>
              <IconButton onClick={() => setAttendeesDialogOpen(false)} size="small">
                <Close fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ p: 0 }}>
          {attendeesLoading ? (
            <Box p={3} display="flex" flexDirection="column" gap={1.5}>
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} variant="rectangular" height={64} sx={{ borderRadius: 2 }} />
              ))}
            </Box>
          ) : (
            <>
              {/* Summary stats */}
              {(() => {
                const ticketCount = (list: AttendeeBooking[]) =>
                  list.reduce((sum, b) => sum + (b.quantity ?? 1), 0);
                const total = ticketCount(attendees);
                const booked = ticketCount(attendees.filter(b => b.bookingStatus === 'BOOKED'));
                const checkedIn = ticketCount(attendees.filter(b => b.bookingStatus === 'CHECKED_IN'));
                const noShow = ticketCount(attendees.filter(b => b.bookingStatus === 'NO_SHOW'));
                const cancelled = ticketCount(attendees.filter(b => b.bookingStatus === 'CANCELLED'));
                return (
                  <Box display="flex" gap={1.5} p={2.5} flexWrap="wrap" bgcolor="#FAFAFA">
                    {[
                      { label: 'Total',      count: total,     color: '#616161', bg: '#F5F5F5' },
                      { label: 'Booked',     count: booked,    color: '#1565C0', bg: '#E3F2FD' },
                      { label: 'Checked In', count: checkedIn, color: '#2E7D32', bg: '#E8F5E9' },
                      { label: 'No Show',    count: noShow,    color: '#5D4037', bg: '#EFEBE9' },
                      { label: 'Cancelled',  count: cancelled, color: '#C62828', bg: '#FFEBEE' },
                    ].map(s => (
                      <Box
                        key={s.label}
                        sx={{
                          px: 2,
                          py: 1,
                          borderRadius: 2,
                          bgcolor: s.bg,
                          border: `1px solid ${s.color}33`,
                          minWidth: 86,
                          textAlign: 'center',
                        }}
                      >
                        <Typography variant="h6" fontWeight={700} color={s.color} lineHeight={1.2}>
                          {s.count}
                        </Typography>
                        <Typography variant="caption" sx={{ color: s.color, fontWeight: 600 }}>
                          {s.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                );
              })()}

              <Divider />

              {/* Search */}
              <Box px={2.5} py={1.5}>
                <TextField
                  placeholder="Search by name, email or phone…"
                  value={attendeesSearchTerm}
                  onChange={(e) => setAttendeesSearchTerm(e.target.value)}
                  size="small"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Attendee list */}
              <Box sx={{ overflowY: 'auto', maxHeight: '52vh' }}>
                {filteredAttendees.length === 0 ? (
                  <Box p={4} textAlign="center">
                    <Typography color="text.secondary">
                      {attendeesSearchTerm ? 'No attendees match your search' : 'No bookings yet for this event'}
                    </Typography>
                  </Box>
                ) : (
                  filteredAttendees.map((b, idx) => {
                    const sc = bkStatusChip(b.bookingStatus);
                    const payMode = b.transaction?.modeOfPayment;
                    const payColors: Record<string, string> = {
                      online: '#1976D2', cash: '#388E3C', free: '#7B1FA2',
                      pack: '#F57C00', wallet: '#0097A7',
                    };
                    const initial = (b.user?.name || '?').charAt(0).toUpperCase();
                    const avatarBg = userAvatarColor(b.user?.name || '?');
                    const qty = b.quantity && b.quantity > 1 ? b.quantity : null;
                    return (
                      <Box key={b._id}>
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1.5}
                          px={2.5}
                          py={1.5}
                          sx={{ '&:hover': { bgcolor: '#FAFAFA' }, transition: 'background 0.15s' }}
                        >
                          <Avatar
                            src={b.user?.image}
                            sx={{ width: 40, height: 40, bgcolor: avatarBg, fontSize: '0.9rem', fontWeight: 700, flexShrink: 0 }}
                          >
                            {initial}
                          </Avatar>
                          <Box flex={1} minWidth={0}>
                            <Box display="flex" alignItems="center" gap={0.75}>
                              <Typography variant="body2" fontWeight={700} noWrap>{b.user?.name}</Typography>
                              {qty && (
                                <Chip
                                  label={`×${qty} tickets`}
                                  size="small"
                                  sx={{
                                    bgcolor: '#FFF8E1',
                                    color: '#F57F17',
                                    fontWeight: 700,
                                    fontSize: '0.65rem',
                                    height: 18,
                                    borderRadius: 1,
                                    flexShrink: 0,
                                  }}
                                />
                              )}
                            </Box>
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                              {b.user?.email}
                              {b.user?.phoneNumber ? ` · ${b.user.phoneNumber}` : ''}
                            </Typography>
                          </Box>
                          <Chip
                            label={sc.label}
                            size="small"
                            sx={{
                              bgcolor: sc.bg,
                              color: sc.color,
                              fontWeight: 700,
                              fontSize: '0.7rem',
                              height: 22,
                              borderRadius: 1,
                              flexShrink: 0,
                            }}
                          />
                          {payMode && (
                            <Chip
                              label={payMode.charAt(0).toUpperCase() + payMode.slice(1)}
                              size="small"
                              variant="outlined"
                              sx={{
                                color: payColors[payMode] ?? '#616161',
                                borderColor: payColors[payMode] ?? '#bdbdbd',
                                fontSize: '0.68rem',
                                height: 20,
                                borderRadius: 1,
                                flexShrink: 0,
                              }}
                            />
                          )}
                          {b.transaction?.basePrice != null && (
                            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                              ₹{b.transaction.basePrice.toLocaleString('en-IN')}
                            </Typography>
                          )}
                          <Typography
                            variant="caption"
                            color="text.disabled"
                            sx={{ flexShrink: 0, fontFamily: 'monospace', fontSize: '0.67rem' }}
                          >
                            #{(b.bookingId || b._id).slice(-6).toUpperCase()}
                          </Typography>
                          <Tooltip title="Update status">
                            <IconButton size="small" onClick={() => openAttStatusDialog(b)} sx={{ flexShrink: 0 }}>
                              <Edit sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        {idx < filteredAttendees.length - 1 && <Divider sx={{ mx: 2.5 }} />}
                      </Box>
                    );
                  })
                )}
              </Box>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Typography variant="caption" color="text.secondary" flex={1}>
            {attendees.reduce((sum, b) => sum + (b.quantity ?? 1), 0)} ticket
            {attendees.reduce((sum, b) => sum + (b.quantity ?? 1), 0) !== 1 ? 's' : ''} total
            {' · '}
            {attendees.length} booking{attendees.length !== 1 ? 's' : ''}
          </Typography>
          <Button onClick={() => setAttendeesDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Attendee Status Update Sub-Dialog */}
      <Dialog
        open={attStatusDialogOpen}
        onClose={() => !isUpdatingAttStatus && setAttStatusDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Update Booking Status</DialogTitle>
        <DialogContent>
          {attBookingToUpdate && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Typography variant="body2" color="textSecondary">
                Booking: <strong>#{(attBookingToUpdate.bookingId || attBookingToUpdate._id).slice(-6).toUpperCase()}</strong>
                <br />User: <strong>{attBookingToUpdate.user?.name}</strong>
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>New Status</InputLabel>
                <Select
                  value={attNewStatus}
                  label="New Status"
                  onChange={(e) => {
                    const val = e.target.value;
                    setAttNewStatus(val);
                    if (val === 'CANCELLED') setAttCreditClassBack(true);
                    else if (val === 'BOOKED' || val === 'CHECKED_IN') setAttCreditClassBack(false);
                  }}
                >
                  <MenuItem value="BOOKED">Booked</MenuItem>
                  <MenuItem value="CHECKED_IN">Checked In</MenuItem>
                  <MenuItem value="CANCELLED">Cancelled</MenuItem>
                  <MenuItem value="NO_SHOW">No Show</MenuItem>
                </Select>
              </FormControl>
              {attNewStatus === 'CANCELLED' && attBookingToUpdate.bookingStatus !== 'CANCELLED' && (
                <Box sx={{ border: '1px solid', borderColor: 'warning.main', borderRadius: 1, p: 1.5, bgcolor: 'warning.50' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={attCreditClassBack}
                        onChange={(e) => setAttCreditClassBack(e.target.checked)}
                        color="warning"
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Credit 1 class back to user's pack</Typography>}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                    Check this if the booking used a pack class that should be returned.
                  </Typography>
                </Box>
              )}
              {attBookingToUpdate.bookingStatus === 'CANCELLED' &&
                (attNewStatus === 'BOOKED' || attNewStatus === 'CHECKED_IN') && (
                <Box sx={{ border: '1px solid', borderColor: 'info.main', borderRadius: 1, p: 1.5, bgcolor: 'info.50' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={attCreditClassBack}
                        onChange={(e) => setAttCreditClassBack(e.target.checked)}
                        color="info"
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Deduct 1 class from user's pack</Typography>}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                    Check this if the class was previously credited back when the booking was cancelled.
                  </Typography>
                </Box>
              )}
              <TextField
                label="Reason (optional)"
                value={attStatusReason}
                onChange={(e) => setAttStatusReason(e.target.value)}
                size="small"
                fullWidth
                multiline
                rows={2}
                placeholder="Reason for status change..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAttStatusDialogOpen(false)} disabled={isUpdatingAttStatus}>Cancel</Button>
          <Button
            onClick={handleAttStatusConfirm}
            variant="contained"
            disabled={isUpdatingAttStatus || !attNewStatus}
          >
            {isUpdatingAttStatus ? 'Updating…' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onClose={() => !isDeleting && setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: 'error.main' }}>Delete Event</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>"{eventToDelete?.eventName}"</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained" disabled={isDeleting}>
            {isDeleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <ManualBookingDialog
        open={manualBookingOpen}
        onClose={() => {
          setManualBookingOpen(false);
          setManualBookingEvent(null);
          setManualBookingStudio(null);
        }}
        onSuccess={() => {
          fetchEvents();
          setManualBookingOpen(false);
          setManualBookingEvent(null);
          setManualBookingStudio(null);
        }}
        prefilledEvent={manualBookingEvent ?? undefined}
        prefilledStudio={manualBookingStudio ?? undefined}
      />
    </Box>
  );
};

export default EventsPage;
